import FormControl from '@mui/material/FormControl'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DatePicker, DatePickerProps, DatePickerSlotProps } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { getYear, isValid, setYear } from 'date-fns'
import { ReactElement } from 'react'
import { Field } from 'react-final-form'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { FinalFormInput } from 'src/shared/form/control/index'
import { DateUtils } from 'src/shared/utils/DateUtils'
import { useUserLocale } from 'src/user/UserContext'
import { DateView } from '@mui/x-date-pickers'
import { getDateFnsLocale } from 'src/shared/utils/DateFnsLocaleUtils'

type MuiDatePickerPropsToBeExcluded =
  | FinalFormInput
  | 'autoOk'
  | 'maxDateMessage'
  | 'minDateMessage'
  | 'format'
  | 'error'
  | 'rawValue'
  | 'openPicker'

type DatePickerVariant = 'standard' | 'filled' | 'outlined' | undefined

type DateTimePickerFieldProps = Omit<DatePickerProps<Date>, MuiDatePickerPropsToBeExcluded> & {
  name: string
  label: string
  validate?: any
  required?: boolean
  minDate?: Date
  maxDate?: Date
  variant?: DatePickerVariant
  yearOnly?: 'start' | 'end'
  views?: Array<DateView>
  openToView?: DateView
  forwardSlotProps?: DatePickerSlotProps<Date, false> | undefined
}
export const DateTimePickerField = (props: DateTimePickerFieldProps): ReactElement => {
  const { getMessage } = useMessageSource()
  const locale = useUserLocale()
  const {
    required,
    name,
    validate,
    minDate,
    maxDate,
    variant,
    yearOnly,
    views,
    openToView,
    forwardSlotProps,
    ...forwardProps
  } = props

  const rangeStartDate = minDate ?? DateUtils.MIN_DATE
  const rangeEndDate = maxDate ?? DateUtils.MAX_DATE

  return (
    <Field
      name={name}
      validate={validate}
      render={({ input: { value, onChange, onFocus, onBlur }, meta: { touched, error: errorObject } }) => {
        const localOnChange = (date: Date | null) => {
          if (date === null) {
            onChange(null)
          } else if (!isValid(date)) {
            onChange(date)
          } else {
            if (yearOnly) {
              if (yearOnly === 'start') {
                date = setYear(rangeStartDate, getYear(date))
              } else if (yearOnly === 'end') {
                date = setYear(rangeEndDate, getYear(date))
              }
            }
            onChange(DateUtils.formatDate(date))
          }
        }

        let resolvedValue: Date | null
        const parsedDateValue = DateUtils.parseDate(value)
        const yearValue = getYear(parsedDateValue)

        if (value) {
          if (yearOnly === 'start') {
            resolvedValue = setYear(rangeStartDate, yearValue)
          } else if (yearOnly === 'end') {
            resolvedValue = setYear(rangeEndDate, yearValue)
          } else {
            resolvedValue = parsedDateValue
          }
        } else {
          resolvedValue = null
        }

        const error = errorObject && getMessage(errorObject.errorKey, errorObject.params)
        const invalid = Boolean(touched && error)

        const resolvedViews = views ?? (yearOnly ? ['year'] : ['year', 'month', 'day'])

        const resolvedFormat = (() => {
          if (!views && yearOnly) return DateUtils.YEAR_FORMAT
          if (!views) return DateUtils.DATE_FORMAT

          const v = views.join(',')

          if (v === 'year') return DateUtils.YEAR_FORMAT
          if (v === 'month,day' || v === 'day,month') return DateUtils.MONTH_DAY_FORMAT
          if (v === 'year,month' || v === 'month,year') return DateUtils.YEAR_MONTH_FORMAT
          if (v === 'year,month,day' || v === 'year,day,month' || v === 'month,day,year') return DateUtils.DATE_FORMAT

          // fallback
          return DateUtils.DATE_FORMAT
        })()

        return (
          <FormControl error={invalid} onFocus={onFocus} onBlur={onBlur} fullWidth>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={getDateFnsLocale(locale)}>
              <DatePicker<Date>
                {...forwardProps}
                minDate={rangeStartDate}
                maxDate={rangeEndDate}
                value={resolvedValue}
                onChange={localOnChange}
                format={resolvedFormat}
                openTo={openToView}
                views={resolvedViews}
                slotProps={{
                  textField: {
                    error: invalid,
                    name: name,
                    helperText: (invalid && error) || ' ',
                    required: required,
                    variant: variant,
                  },
                  ...forwardSlotProps,
                }}
              />
            </LocalizationProvider>
          </FormControl>
        )
      }}
    />
  )
}
